import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';

/** Shared audit-log viewer for the 3 catalog edit drawers (agents/services/products).
 *  Drop-in tab: pass the entity type + id, get a timeline. Keeps the per-page
 *  forms thin and the history rendering consistent.
 */

export type CatalogEntityType = 'ai_agent' | 'service' | 'product';

interface DiffField {
  before: unknown;
  after: unknown;
}

interface AuditEntry {
  id: string;
  entityType: CatalogEntityType;
  entityId: string;
  entityNaturalId: string | null;
  action: 'CREATE' | 'UPDATE' | 'ARCHIVE' | 'RESTORE' | 'DELETE';
  changedByUserId: string | null;
  changedByEmail: string | null;
  diff: string | null;        // JSON string · {col: {before, after}}
  beforeState: string | null; // JSON string
  afterState: string | null;  // JSON string
  comment: string | null;
  changedAt: string;
}

function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v.length > 60 ? `"${v.slice(0, 57)}…"` : `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]';
    return `[${v.length} items]`;
  }
  return JSON.stringify(v).slice(0, 80);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const ACTION_STYLES: Record<AuditEntry['action'], string> = {
  CREATE: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
  UPDATE: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200',
  ARCHIVE: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  RESTORE: 'bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-200',
  DELETE: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200',
};

export function CatalogHistoryTab({
  entityType,
  entityId,
}: {
  entityType: CatalogEntityType;
  entityId: string;
}) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    apiClient
      .get<AuditEntry[]>(`/admin/catalog-audit/${entityType}/${entityId}?limit=200`)
      .then(({ data }) => setEntries(data))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-muted-foreground">loading history…</p>;
  if (error) return <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>;
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No changes recorded yet · this row may pre-date the audit log (added 2026-06-03).
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        {entries.length} {entries.length === 1 ? 'change' : 'changes'} · most recent first
      </div>
      <ol className="space-y-3">
        {entries.map((e) => (
          <li key={e.id} className="rounded-lg border bg-card p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs font-semibold ${ACTION_STYLES[e.action]}`}>
                  {e.action}
                </span>
                <span className="text-sm">{e.changedByEmail || 'system'}</span>
              </div>
              <time className="font-mono text-xs text-muted-foreground" dateTime={e.changedAt}>
                {formatDate(e.changedAt)}
              </time>
            </div>
            {e.comment && (
              <p className="mt-2 text-sm italic text-muted-foreground">“{e.comment}”</p>
            )}
            <DiffTable diffJson={e.diff} action={e.action} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function DiffTable({ diffJson, action }: { diffJson: string | null; action: AuditEntry['action'] }) {
  if (!diffJson) return null;
  let parsed: Record<string, DiffField>;
  try {
    parsed = JSON.parse(diffJson);
  } catch {
    return <pre className="mt-2 overflow-x-auto rounded bg-muted/40 p-2 text-xs">{diffJson}</pre>;
  }
  const cols = Object.keys(parsed);
  if (cols.length === 0) {
    return action === 'UPDATE'
      ? <p className="mt-2 text-xs text-muted-foreground">No tracked field changed (likely a no-op save).</p>
      : null;
  }
  return (
    <div className="mt-2 overflow-hidden rounded border">
      <table className="w-full text-xs">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-2 py-1 text-left">field</th>
            <th className="px-2 py-1 text-left">before</th>
            <th className="px-2 py-1 text-left">after</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {cols.map((col) => (
            <tr key={col}>
              <td className="px-2 py-1 font-mono">{col}</td>
              <td className="px-2 py-1 font-mono text-muted-foreground">
                {formatValue(parsed[col].before)}
              </td>
              <td className="px-2 py-1 font-mono">{formatValue(parsed[col].after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
